import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  fetchCourses,
  selectCourseLoading,
  selectCourses,
  selectNoCourses,
  selectTotalItems,
  setNavigationSource,
} from "@/store/slices/courseSlice";
import { AppDispatch } from "@store";
import {
  Eye,
  MoreVertical,
  Pencil,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// Will use navigate when implementing add, edit, view functionality
// import { useNavigate } from "react-router";

// Using the CourseModelList from types
import { CourseModelList } from "@/store/slices/courseSlice";
import { useNavigate } from "react-router";
import { isAllSchoolsSelected, selectSchoolId, selectUser, selectUserRole } from "@/store/slices/authSlice";
import {
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  PDFIcon,
  ROLE_PERMISSIONS,
  ROLES,
  successToast,
} from "@/utils/constants";
import { getCourseListDisplayStatus } from "@/service/course.mappers";
import {
  deleteCourseService,
  downloadCourseService,
  toggleCourseActiveService,
} from "@/service/course.service";
import { DownloadCourseQuery } from "@/types/course.model";
import { Button } from "@/components/design-system/button";
import { StatusPill } from "@/components/design-system/status-pill";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Modal from "@/components/design-system/Modal";

// Extended interface for UI display purposes
interface CourseDisplay extends CourseModelList {
  sno?: number;
}

const ITEMS_PER_PAGE = 10;

export default function CourseListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    id?: string;
    label?: string;
  }>({ open: false });
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [courseToToggle, setCourseToToggle] = useState<{
    id: string;
    is_active: boolean;
    label?: string;
  } | null>(null);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  const dispatch = useDispatch<AppDispatch>();
  const courses = useSelector(selectCourses);
  const loading = useSelector(selectCourseLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);
  const user = useSelector(selectUser);
  const noCourses = useSelector(selectNoCourses);
  const schoolId = useSelector(selectSchoolId);
  const isAllSchools = useSelector(isAllSchoolsSelected);

  // Will use navigate when implementing add, edit, view functionality
  const navigate = useNavigate();

  const userPerms = userRole?.permissions as string[];
  const isSuperAdmin = userRole?.code === ROLES.SUPER_ADMIN;

  const canViewCourse = hasMenuPermission(
    ROLE_PERMISSIONS.ACTION_VIEW_COURSE,
    userPerms
  );

  const canEditCourse =
    isSuperAdmin &&
    hasMenuPermission(ROLE_PERMISSIONS.ACTION_EDIT_COURSE, userPerms);

  const canDeleteCourse =
    isSuperAdmin &&
    hasMenuPermission(ROLE_PERMISSIONS.ACTION_DELETE_COURSE, userPerms);

  const canToggleCourseActive =
    isSuperAdmin &&
    hasMenuPermission(ROLE_PERMISSIONS.ACTION_ACTIVE_INACTIVE_COURSE, userPerms);

  const showActionColumn =
    canViewCourse || canEditCourse || canDeleteCourse || canToggleCourseActive;

  const canCreateCourse =
    isSuperAdmin &&
    (hasMenuPermission(ROLE_PERMISSIONS.BUTTON_CREATE_COURSE, userPerms) ||
      hasMenuPermission(ROLE_PERMISSIONS.ACTION_EDIT_COURSE, userPerms));

  const schoolColumn: TableColumn<CourseDisplay> = {
    key: "school_name",
    title: "School Name",
    dataIndex: "school_name",
    minWidth: 250,
    maxWidth: 250,
    sortable: true,
    sortKey: "school_name",
    render: (_, record) => (
      <div
        className="text-[#1F1F1F] text-[16px] font-medium"
        style={{
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
        }}
      >
        {record?.school_name || "N/A"}
      </div>
    ),
  };

  const columns: TableColumn<CourseDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] font-medium text-center text-[16px]">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    ...(isAllSchools ? [schoolColumn] : []),
    {
      key: "grade",
      title: "Grade Name",
      dataIndex: "grade",
      minWidth: 212,
      maxWidth: 212,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium text-[16px]"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.grade}
        </div>
      ),
    },
    {
      key: "terms",
      title: "No of Terms",
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium text-[16px]">
          {record.terms ?? "-"}
        </div>
      ),
    },
    {
      key: "lessons",
      title: "No of Lessons",
      minWidth: 170,
      maxWidth: 170,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium text-[16px]">
          {record.lessons}
        </div>
      ),
    },
    {
      key: "units",
      title: "No of Units",
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium text-[16px]">
          {record.units}
        </div>
      ),
    },
    {
      key: "status",
      title: "Status",
      minWidth: 180,
      maxWidth: 180,
      sortable: true,
      render: (_, record) => {
        const { label, variant } = getCourseListDisplayStatus(record);
        return <StatusPill label={label} variant={variant} />;
      },
    },
    {
      key: "students",
      title: "No of Students Learning",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium text-[16px]">
          {record.students}
        </div>
      ),
    },
  ];

  if (showActionColumn) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 180,
      maxWidth: 180,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center justify-start gap-2">
          {canViewCourse && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full"
              onClick={() => {
                dispatch(setNavigationSource("course-list"));
                navigate(`/course-management/view-course/${record.id}`);
              }}
              aria-label="View course"
              type="button"
            >
              <Eye className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )}

          {canEditCourse && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full"
              onClick={() =>
                navigate(`/course-management/edit/${record.id}`, {
                  state: {
                    is_published: record.is_published,
                    is_active: record.is_active,
                  },
                })
              }
              aria-label="Edit course"
              type="button"
            >
              <Pencil className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )}

          {canDeleteCourse && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full"
              onClick={() =>
                setConfirmDelete({
                  open: true,
                  id: record.id,
                  label: record.grade,
                })
              }
              aria-label="Delete course"
              type="button"
            >
              <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )}

          {canToggleCourseActive && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1 hover:bg-gray-100 rounded-full"
                  aria-label="More actions"
                  type="button"
                >
                  <MoreVertical className="w-6 h-6 text-[#1C1B1F]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[180px] !p-0 rounded-[8px] border border-[#EEE] bg-[#FFF] shadow-[0_30px_40px_0_rgba(0,0,0,0.15)]"
              >
                <DropdownMenuItem
                  className="h-[42px] px-4 py-0 flex items-center justify-between gap-2 cursor-pointer rounded-none hover:bg-transparent focus:bg-transparent [&_svg]:!size-6"
                  disabled={getCourseListDisplayStatus(record).isDraft}
                  onClick={() => {
                    setCourseToToggle({
                      id: record.id,
                      is_active: !record.is_active,
                      label: record.grade,
                    });
                    setActivateModalOpen(true);
                  }}
                >
                  <div className="flex items-center justify-between gap-2 w-full">
                    {record.is_active ? (
                      <>
                        <span>Deactivate</span>
                        <ToggleLeft className="w-6 h-6 text-[#FF152C]" />
                      </>
                    ) : (
                      <>
                        <span>Activate</span>
                        <ToggleRight className="w-6 h-6 text-[#00C41D]" />
                      </>
                    )}
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
    });
  }

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: DownloadCourseQuery = {
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
      };

      if (user?.school_id) {
        queryParams.schoolId = user.school_id;
      }

      const response = await downloadCourseService(type, queryParams);

      downloadFileFromBlob(response, type, "course_report");

      successToast(
        "Export Successful",
        `Courses exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting courses as ${type}:`, error);
    }
  };

  // Export options
  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Handle sorting when column headers are clicked
  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config || undefined);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map frontend column keys to backend API sorting keys
  const getSortField = (
    key: string
  ): "GRDE" | "ACT" | "STND" | "LSN" | "UNT" | "SCH" | undefined => {
    const sortFieldMap: Record<
      string,
      "GRDE" | "ACT" | "STND" | "LSN" | "UNT" | "SCH"
    > = {
      grade: "GRDE",
      lessons: "LSN",
      units: "UNT",
      students: "STND",
      is_active: "ACT",
      school_name: "SCH",
    };
    return sortFieldMap[key];
  };

  const refetchCourses = () => {
    dispatch(
      fetchCourses({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue,
        fromDate,
        toDate,
        sortBy: sortConfig?.key ? getSortField(sortConfig.key) : undefined,
      })
    );
  };

  const handleToggleCourseActive = async () => {
    if (!courseToToggle) return;

    setToggleLoading(true);
    try {
      await toggleCourseActiveService(courseToToggle.id, courseToToggle.is_active);
      // successToast(
      //   courseToToggle.is_active ? "Course activated" : "Course inactivated",
      //   courseToToggle.is_active
      //     ? "The course is now active."
      //     : "The course is now deactivate."
      // );
      successToast("Course status updated successfully", "");
      setActivateModalOpen(false);
      setCourseToToggle(null);
      refetchCourses();
    } catch (e) {
      console.error(e);
    } finally {
      setToggleLoading(false);
    }
  };

  // Fetch courses on component mount and when search, pagination, date range, or sorting changes
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    // Create an AbortController to cancel duplicate requests
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Prepare sorting parameters if sort config exists
    const sortParams =
      sortConfig && sortConfig.key
        ? {
            sortBy: getSortField(sortConfig.key),
          }
        : {};

    // Use a timeout to debounce the API call
    const timeoutId = setTimeout(() => {
      // Only proceed if the request hasn't been aborted
      if (!signal.aborted) {
        dispatch(
          fetchCourses({
            page: currentPage,
            pageSize: ITEMS_PER_PAGE,
            search: searchValue,
            fromDate: fromDate,
            toDate: toDate,
            ...sortParams,
          })
        );
      }
    }, 0); // 0ms delay still puts it in the next event loop cycle

    // Cleanup function to abort any pending requests when dependencies change
    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [dispatch, currentPage, searchValue, fromDate, toDate, sortConfig, schoolId]);

  if (noCourses) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No courses assigned yet.
              </p>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Your assigned courses will be listed here.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title="Course List"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        rightAction={
          <>
            {/* Export Button */}
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_COURSE,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}

            {canCreateCourse && (
              <Button
                type="button"
                variant="primary"
                size="md"
                className="h-[48px] px-6 bg-[#5C88C5] text-white"
                onClick={() => navigate("/course-management/create")}
              >
                Create Course
              </Button>
            )}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          {/* {loading ? (
            <div className="flex items-center justify-center h-[calc(100vh-300px)] w-full">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading courses...</p>
              </div>
            </div>
          ) : ( */}
          <Table
            columns={columns}
            data={courses} // Use data from Redux store
            rowKey="id"
            showHeader={true} // Hide header as we're showing it separately above
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading} // We're handling loading state ourselves
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          {/* )} */}
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Courses"
      />

      <Modal
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        title="Delete Course"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure you want to delete{" "}
            <span className="text-[#1F1F1F] font-semibold">
              {confirmDelete.label || "this course"}
            </span>
            ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setConfirmDelete({ open: false })}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={async () => {
              try {
                if (confirmDelete.id) await deleteCourseService(confirmDelete.id);
                setConfirmDelete({ open: false });
                dispatch(
                  fetchCourses({
                    page: currentPage,
                    pageSize: ITEMS_PER_PAGE,
                    search: searchValue,
                    fromDate,
                    toDate,
                    sortBy: sortConfig?.key
                      ? getSortField(sortConfig.key)
                      : undefined,
                  })
                );
              } catch (e) {
                console.error(e);
              }
            }}
          >
            Yes, Delete
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        title={courseToToggle?.is_active ? "Activate" : "Deactivate"}
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            {courseToToggle?.is_active ? (
              <>
                Are you sure you want to activate{" "}
                <span className="text-[#1F1F1F] font-semibold">
                  {courseToToggle.label || "this course"}
                </span>
                ?
              </>
            ) : (
              <>
                Are you sure you want to deactivate{" "}
                <span className="text-[#1F1F1F] font-semibold">
                  {courseToToggle?.label || "this course"}
                </span>
                ?
                <br />
                Once deactivated, this course will no longer be available to
                students and teachers.
              </>
            )}
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setActivateModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleToggleCourseActive()}
            disabled={toggleLoading}
          >
            {toggleLoading
              ? "Processing..."
              : courseToToggle?.is_active
                ? "Yes, Activate"
                : "Yes, Deactivate"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
